/**
 * Bewertungsklasse — Eigentum ↔ Rendite (order MFH-Rendite B1, owner 23-9).
 *
 * The owner's words: «een net filter- of schakelsysteem tussen MFH en
 * Wohnungen/EFH — MFH is normaal een RENDITE-object, de rest normaal
 * EIGENTUM». IAZI draws the same line: its Web App knows Einfamilienhaus,
 * Eigentumswohnung and Renditeliegenschaft, and values the first two
 * hedonically (Verkehrswert) and the third by capitalised rents (Ertragswert).
 *
 * ONE source: the Bewertungsformular, the list and its filter, the detail page
 * and the backend's `?klasse=` all read from here — no second list in a
 * component.
 */
export const BEWERTUNGSKLASSEN = ['eigentum', 'rendite'];
/** The object types of each class — the inverse of {@link klasseVon}. */
export const KLASSE_OBJEKTTYPEN = {
    eigentum: ['efh', 'ewg'],
    rendite: ['mwg'],
};
export const BEWERTUNGSKLASSE_LABEL = {
    eigentum: 'Eigentum — Einfamilienhaus / Eigentumswohnung',
    rendite: 'Rendite — Mehrfamilienhaus (Renditeliegenschaft)',
};
/** The short word for a badge or a filter button. */
export const BEWERTUNGSKLASSE_KURZ = {
    eigentum: 'Eigentum',
    rendite: 'Rendite',
};
/** What IAZI does with each class — one line under the choice in the form. */
export const BEWERTUNGSKLASSE_METHODE = {
    eigentum: 'IAZI schätzt den hedonischen Verkehrswert.',
    rendite: 'IAZI schätzt den Ertragswert (kapitalisierte Mieten).',
};
export const OBJEKTTYP_LABEL = {
    efh: 'Einfamilienhaus',
    ewg: 'Eigentumswohnung',
    mwg: 'Mehrfamilienhaus',
};
/**
 * Order 13-10 (owner): the LONG word of the object type — the CRM's Objekttyp
 * select and the show page. The MFH mask IS HEDO's «Renditeliegenschaft» (model
 * cv), and the word says so where the type is chosen and read back. EFH and EWG
 * keep their word. The short {@link OBJEKTTYP_LABEL} stays everywhere else: the
 * list's type cell, the Bewertungsbericht and the mails.
 */
export const OBJEKTTYP_LABEL_LANG = {
    ...OBJEKTTYP_LABEL,
    mwg: 'Mehrfamilienhaus / Renditeliegenschaft',
};
/** efh/ewg → eigentum, mwg → rendite; anything else (a legacy code, empty) → null. */
export function klasseVon(objektTyp) {
    for (const k of BEWERTUNGSKLASSEN) {
        if (KLASSE_OBJEKTTYPEN[k].includes(objektTyp))
            return k;
    }
    return null;
}
// ── The subcategory of a Renditeliegenschaft ────────────────────────────────
/**
 * IAZI's hidden `SubCat` (1 Wohnen · 2 Gemischt · 3 Büro/Gewerbe) follows from
 * «Mietanteil Geschäft (%)». Boundaries measured by typing in the Web App
 * (23-9): 15 → 1, 15.1 → 2, 84.9 → 2, 85 → 3 — so Wohnen up to AND including
 * 15, Büro/Gewerbe from AND including 85.
 */
export const RENDITE_SUBKATEGORIEN = ['wohnen', 'gemischt', 'buero_gewerbe'];
export const RENDITE_SUBKATEGORIE_LABEL = {
    wohnen: 'Wohnen',
    gemischt: 'Gemischt',
    buero_gewerbe: 'Büro/Gewerbe',
};
/** Mietanteil Geschäft up to and including this value is «Wohnen». */
export const MIETANTEIL_WOHNEN_BIS = 15;
/** Mietanteil Geschäft from and including this value is «Büro/Gewerbe». */
export const MIETANTEIL_BUERO_AB = 85;
/**
 * Order IAZI 25-9 PR 2 (master 12:08, K5): do the parking rents (Parkplätze,
 * Garagen, Einstellhalle) belong to the Mietertrag? ONE constant: the import's
 * Mietanteil Geschäft puts them in the denominator exactly when the Mietertrag
 * includes them, and the label of the field (Checkliste and
 * Bewertungsformular) says which it is. IAZI 29-9 (M. Molteni, answer 2)
 * CONFIRMED it: `rentAnnual` includes the Einstellhallen-/Aussenparkplätze,
 * and at `rentRepartitionPercent` the parking rents do NOT count as Geschäft —
 * the import's formula (parking in the denominator only) already is that.
 */
export const MIETERTRAG_INKL_PARKIEREN = true;
/**
 * B11 (IAZI 29-9, answer 1): `rentAnnual` is the «Mietwert nachhaltig» — the
 * yearly NET rent without Nebenkosten; the effective Mietertrag is only
 * information. The field keeps its name `jahresmiete`; its words change in
 * both forms (Checkliste, Bewertungsformular), and the Bericht (B9) takes
 * {@link JAHRESMIETE_LABEL_KURZ} over. The parking part follows the constant.
 */
export const JAHRESMIETE_LABEL = `Mietwert nachhaltig (Netto-Jahresmietertrag ohne Nebenkosten, ${MIETERTRAG_INKL_PARKIEREN ? 'inkl.' : 'ohne'} Parkplätze)`;
/** The short name — a PDF table row, a prefill hint, the «ersetzt» label of `rentAnnual`. */
export const JAHRESMIETE_LABEL_KURZ = 'Mietwert nachhaltig';
/**
 * The hint under the Mietwert, in both forms: IAZI's own sentence (answer 1),
 * then RV612c's rounding sentence of the franken field.
 */
export const MIETERTRAG_HINWEIS = 'IAZI rechnet mit dem nachhaltigen Mietwert; der effektive Mietertrag ist nur Information. In ganzen Franken (Rappen werden gerundet).';
/** B11 (IAZI 29-9, answer 2): the sentence under «Mietanteil Geschäft», in both forms. */
export const MIETANTEIL_PARKIEREN_HINWEIS = 'Parkplatzmieten zählen nicht zum Geschäftsanteil.';
/**
 * B11 (IAZI 29-9, answer 3): the Nutzungsart — selbstgenutzt / fremdvermietet,
 * the Checkliste's «Objektkategorie» — has no field in A1/A2/A3 and no
 * influence on the valuation (it still steers the Exposé, E19).
 */
export const NUTZUNGSART_HINWEIS = 'Ohne Einfluss auf die Bewertung.';
/**
 * The subcategory for a Mietanteil Geschäft. Empty → «Wohnen» AS AN
 * ASSUMPTION (`annahme: true`): that is what the Web App falls back to, and
 * the screen must show it as an assumption, never as a measurement.
 */
export function renditeSubkategorie(mietanteilGeschaeft) {
    if (mietanteilGeschaeft === null || mietanteilGeschaeft === undefined) {
        return { kategorie: 'wohnen', annahme: true };
    }
    if (mietanteilGeschaeft <= MIETANTEIL_WOHNEN_BIS)
        return { kategorie: 'wohnen', annahme: false };
    if (mietanteilGeschaeft < MIETANTEIL_BUERO_AB)
        return { kategorie: 'gemischt', annahme: false };
    return { kategorie: 'buero_gewerbe', annahme: false };
}
/**
 * IAZI-order 8-10 A4: the `subCat` the cv model receives (CVInputPPCH, HEDO
 * «Kategorie»): 1 Wohnen · 2 Gemischt · 3 Büro/Gewerbe — from
 * {@link renditeSubkategorie}, ONE source for the thresholds 15 / 85.
 */
export const IAZI_SUBCAT = {
    wohnen: 1,
    gemischt: 2,
    buero_gewerbe: 3,
};
/**
 * The cv `subCat` for a Mietanteil Geschäft. Without a Mietanteil there is no
 * subCat — never the «Wohnen (Annahme)» of the display: the Pflicht gate holds
 * such a call before IAZI, and this throws as the last line of defence.
 */
export function iaziSubCat(mietanteilGeschaeft) {
    const { kategorie, annahme } = renditeSubkategorie(mietanteilGeschaeft);
    if (annahme)
        throw new Error('iazi_subcat_ohne_mietanteil');
    return IAZI_SUBCAT[kategorie];
}
/** «Wohnen» · «Gemischt» · «Büro/Gewerbe», or «Wohnen (Annahme)» when empty. */
export function renditeSubkategorieLabel(mietanteilGeschaeft) {
    const { kategorie, annahme } = renditeSubkategorie(mietanteilGeschaeft);
    const label = RENDITE_SUBKATEGORIE_LABEL[kategorie];
    return annahme ? `${label} (Annahme)` : label;
}
/**
 * Order MFH-Rendite PR 2 (23-9): the Renditeliegenschaft's fields (ten since 8-10) — ONE
 * name in the Bewertung (`paidMerkmale`) and in the Checkliste (`objekt`), the
 * B3-4 principle, so both directions copy instead of bridging. In the Web App
 * order: Masse (mix, HNF2–6) · Gebäude (Aufzüge) · Miete.
 */
export const RENDITE_FELDER = [
    'wohnungsspiegel',
    'bueroflaeche',
    'gewerbeflaeche',
    'verkaufsflaeche',
    'unterrichtsflaeche',
    'medizinflaeche',
    'aufzuege',
    'jahresmiete',
    // IAZI-order 8-10 (B4): HEDO «Mietertrag effektiv» — information, never sent.
    'mietertragEffektiv',
    'mietanteilGeschaeft',
];
